'use client';

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PencilIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { FullScreenContext } from '@/app/contexts';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { Slide } from '@/shared/types';
import { formatSlideDateUtc } from '@/shared/clientFunctions';
import MediaPane from './panes/mediaPane';
import DescriptionPane from './panes/descriptionPane';
import SourcesPane from './panes/sourcesPane';
import TitlePane from './panes/titlePane';
import TimePane from './panes/timePane';
import DatePane from './panes/datePane';
import HeaderPane from './panes/headerPane';
import LocationPane from './panes/locationPane';

const HTL_INTERACTIVE_FOCUS_CLASS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--htl-marker)]';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('disabled') && element.tabIndex !== -1,
  );

interface TPHorizontalTimelineEventModalProps {
  slide: Slide;
  onClose: () => void;
  onEdit: (slide: Slide) => void;
  /** Writes a media-context patch back onto the event. Omitted for the synthetic title slide. */
  onUpdateSlide?: (slide: Slide) => void;
  /** Current PDF page for one clip on this event. */
  onViewingPageChange?: (page: number, mediaIndex: number) => void;
}

/**
 * Merge a media-context patch onto one clip.
 * Returns null for the title slide and for an index the slide does not have.
 */
export function applyMediaContextPatch(
  slide: Slide,
  mediaIndex: number,
  patch: Record<string, unknown>,
): Slide | null {
  if (slide.id === 'TITLE_SLIDE' || !slide.media) return null;
  const current = slide.media[mediaIndex];
  if (!current) return null;
  const nextMedia = slide.media.map((item, index) =>
    index === mediaIndex
      ? {
          ...item,
          mediaContext: {
            ...(item.mediaContext ?? {}),
            ...patch,
          },
        }
      : item,
  );
  return { ...slide, media: nextMedia };
}

/**
 * Apply a patch to the newest slide, including one emitted earlier in this
 * turn. A prop snapshot is stale until the parent re-renders, so a Full Media
 * save right after Use Capture would otherwise merge onto the pre-capture slide.
 */
export function patchLatestSlideMediaContext(
  latestSlideRef: { current: Slide },
  mediaIndex: number,
  patch: Record<string, unknown>,
): Slide | null {
  const next = applyMediaContextPatch(latestSlideRef.current, mediaIndex, patch);
  if (next) latestSlideRef.current = next;
  return next;
}

const getHeaderLabel = (slide: Slide): string => {
  if (slide.id === 'TITLE_SLIDE') return slide.slideTitle || 'Case Introduction';
  if (slide.slideSubTitle && slide.slideSubTitle.trim() !== '') return slide.slideSubTitle;
  if (slide.date) {
    const formatted = formatSlideDateUtc(slide.date, 'MMMM D, YYYY');
    if (formatted) return formatted;
  }
  return slide.slideTitle || 'Event';
};

/**
 * Read-only expanded view of a timeline event. Mirrors the presentation panes
 * (entities, title, media, description, sources) so it matches the media-item
 * styling, but laid out in a large modal that fills more of the window with the
 * sources collapsed at the bottom.
 */
const TPHorizontalTimelineEventModal: React.FC<TPHorizontalTimelineEventModalProps> = ({
  slide,
  onClose,
  onEdit,
  onUpdateSlide,
  onViewingPageChange,
}) => {
  const t = useTimePilotT();
  const { isFullscreen } = useContext(FullScreenContext) || { isFullscreen: false };
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const mediaColumnRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const [mediaBox, setMediaBox] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    setMounted(true);
  }, []);

  const isTitleSlide = slide.id === 'TITLE_SLIDE';
  const hasMedia = !!slide.media && slide.media.length > 0;
  const hasDescription = !!slide.slideDescription?.trim();
  const hasSources = !!slide.sources && slide.sources.length > 0;
  const hasTitle = !!(slide.slideTitle || slide.slideSubTitle);
  const latestSlideRef = useRef(slide);
  const incomingSlideRef = useRef(slide);
  if (slide !== incomingSlideRef.current) {
    incomingSlideRef.current = slide;
    latestSlideRef.current = slide;
  }

  const handleUpdateMediaContext = useCallback((mediaIndex: number, patch: Record<string, unknown>) => {
    if (!onUpdateSlide) return;
    const next = patchLatestSlideMediaContext(latestSlideRef, mediaIndex, patch);
    if (next) onUpdateSlide(next);
  }, [onUpdateSlide]);

  // Lock background scroll and close on Escape. Media size comes from the
  // column itself (below), not from a fraction of the window.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isFullscreen) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isFullscreen, onClose]);

  useEffect(() => {
    const column = mediaColumnRef.current;
    if (!mounted || !hasMedia || !column) return;
    const measure = () => {
      const width = Math.floor(column.clientWidth);
      const height = Math.floor(column.clientHeight);
      setMediaBox((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(column);
    return () => observer.disconnect();
  }, [hasMedia, mounted, slide.id]);

  useEffect(() => {
    if (!mounted) return;

    const frame = requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [mounted]);

  useEffect(() => {
    if (!mounted || isFullscreen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = getFocusableElements(dialogRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isFullscreen, mounted]);

  if (!mounted) return null;

  const metadataRow = (
    <div className="flex flex-row flex-wrap items-baseline justify-start gap-[10px]">
      {!isTitleSlide && slide.slideSubTitle && <HeaderPane slide={slide} />}
      {!isTitleSlide && !slide.slideSubTitle && <DatePane slide={slide} />}
      {!isTitleSlide && !slide.slideSubTitle && slide.time && slide.isTimeValid && (
        <TimePane slide={slide} />
      )}
      {(slide.location?.name || slide.location?.address) && <LocationPane slide={slide} />}
    </div>
  );

  const textColumn = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
      {metadataRow}
      {hasTitle && <TitlePane slide={slide} />}
      {hasDescription && <DescriptionPane slide={slide} />}
      {hasSources && (
        <div data-testid="HorizontalTimelineEventModalSources" className="mt-auto flex w-full justify-end pt-2">
          <SourcesPane slide={slide} />
        </div>
      )}
      {!hasTitle && !hasMedia && !hasDescription && (
        <div className="flex min-h-[160px] flex-col items-center justify-center text-white/70">
          <p className="text-lg">{t('timeline.noEventContent')}</p>
        </div>
      )}
    </div>
  );

  // Portal to document.body so the overlay escapes the main content stacking
  // context (z-[30000]) at nav level (z-[99999]), below fullscreen media
  // (z-[100000]) so media expanded from this modal stacks on top.
  return createPortal(
    <AnimatePresence>
      <motion.div
        data-testid="HorizontalTimelineEventModalOverlay"
        className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 p-4 sm:p-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={onClose}
      >
        <motion.div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={`Event details: ${getHeaderLabel(slide)}`}
          data-testid="HorizontalTimelineEventModal"
          className="htl-modal flex h-[800px] max-h-[88vh] w-full max-w-[1100px] flex-col overflow-hidden rounded-[8px]"
          initial={{ opacity: 0, scale: 0.97, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 8 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          onClick={(event) => event.stopPropagation()}
        >
          {/* Pane header (Figma .PaneHeader): title + titleActions on the left, close on the right. */}
          <div className="htl-event-header flex h-10 shrink-0 items-center justify-between gap-2 rounded-t-[8px] pl-4 pr-2">
            <div className="flex min-w-0 items-center gap-1">
              <span className="truncate text-sm font-bold leading-5 text-white">{getHeaderLabel(slide)}</span>
              <Button
                type="button"
                iconOnly
                size="sm"
                style="text"
                color="light"
                background="inverse"
                data-testid="HorizontalTimelineEventModalEdit"
                aria-label={t('timeline.editEvent')}
                onClick={() => onEdit(slide)}
                className={HTL_INTERACTIVE_FOCUS_CLASS}
              >
                <PencilIcon weight="duotone" />
              </Button>
            </div>
            <Button
              type="button"
              ref={closeButtonRef}
              iconOnly
              size="sm"
              style="text"
              color="light"
              background="inverse"
              data-testid="HorizontalTimelineEventModalClose"
              aria-label={t('eventModal.close')}
              onClick={onClose}
              className={HTL_INTERACTIVE_FOCUS_CLASS}
            >
              <XIcon weight="bold" />
            </Button>
          </div>

          {/* Body: two columns when media is present (media left, content right). */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
            {hasMedia ? (
              <div className="flex min-h-0 flex-1 flex-col gap-4 sm:flex-row sm:items-stretch">
                <div
                  ref={mediaColumnRef}
                  className="htl-modal-media flex min-h-[240px] w-full min-w-0 flex-1 flex-col overflow-hidden sm:min-h-0 sm:flex-[3]"
                >
                  {/* Legacy MediaDisplay for unmigrated types. JSON uses the theme
                      viewer inside MediaDisplay, gated by media-player-transition.
                      Wait for a measured box so the viewer is not fitted to a
                      fraction of the window that the column then clips. */}
                  {mediaBox.height > 0 && (
                    <MediaPane
                      key={slide.id}
                      slide={slide}
                      isPresentation
                      isFullscreen={false}
                      fullscreenMediaItem={null}
                      setFullscreenMediaItem={() => {}}
                      presentationHeight={mediaBox.height}
                      containerWidth={mediaBox.width > 0 ? mediaBox.width : undefined}
                      onUpdateMediaContext={isTitleSlide ? undefined : handleUpdateMediaContext}
                      onViewingPageChange={onViewingPageChange}
                    />
                  )}
                </div>
                <div className="custom-scrollbar2 flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto sm:flex-[2]">
                  {textColumn}
                </div>
              </div>
            ) : (
              <div className="custom-scrollbar2 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
                {textColumn}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};

export default TPHorizontalTimelineEventModal;
